import { STORY_SOURCE, story } from '@/content/storyBuilder'

// The eighth written page. Everything below comes from the four-part full-page
// capture of digitopia.co/customers/lc-waikiki/ sent on 2026-08-28 — "birebir
// tıpa tıp aynısını tasarla" — with the one photograph on it, the garment rack
// under "Who is LC Waikiki?". The film already sat in the header, which is
// where the reference puts it, so nothing above the fold changed but the lede.
//
// Its shape is the reference's and not the template's usual: no `intro` band
// and no grey panels anywhere, because the reference runs the whole story on
// the page — one centred section introducing the company, the picture under
// it, and then four left-ranged sections of the CDO speaking in the first
// person. The closing quote is not a `section`: Şerafettin Özer's words have
// been in `content/testimonials.ts` keyed to this slug since the Hall of Fame
// shipped, LC Waikiki's mark and his portrait included, and `StoryBody` draws
// that card — the testimonial template the same request asked for at the foot
// of the page.
export const added = story('lc-waikiki', 'LC Waikiki', {
  doc: '0584eaf0-a494-485f-9701-13f832581466-LC Waikiki and Digitopia.pdf',
  video: 'QdE0TzzXoYE',
  lede: ['Thinking about the past, the present, and our digital future…'],
  sections: [
    {
      heading: 'Who is LC Waikiki?',
      centred: true,
      body: [
        'Founded by three partners of French origin in 1988, LC Waikiki started its commercial '
        + 'life as a wholesale brand. Tema Tekstil, which is connected to Taha Group and is the '
        + 'producer as well as licensor of LC Waikiki in Turkey, bought the worldwide rights of '
        + 'the LC Waikiki brand in 1997. After the purchase of all the rights of the brand, the '
        + 'name of Tema Tekstil, belonging to Taha Group, was changed to “LC Waikiki Retailing '
        + 'Services”.',
      ],
      photo: {
        src: '/photo/lc-waikiki-garment-rack.webp',
        alt: 'A long rail of shirts and jackets on wooden hangers in a daylit showroom, running '
          + 'from cream and beige at the front to navy and black at the far end.',
      },
    },
    {
      body: [
        'Taha Group, founded in 1988, bought LC Waikiki, which was born in Paris but grew in '
        + 'Istanbul, and brought it to its current level. Today, LC Waikiki acts in line with '
        + 'the vision of becoming one of Europe’s three most successful clothing brands until '
        + '2023. With the mission “everyone deserves to dress well”, the brand continues to grow '
        + 'with its own stores domestic and abroad, and with its franchise partners in countries '
        + 'with legal obligations. With the factories established in various cities of Anatolia, '
        + 'LC Waikiki continues to work on the production and export of the world’s leading '
        + 'brands. As of 2020, LC Waikiki is proud to reach 18.2 billion TL net sales, more than '
        + '50 thousand employees, 845 million USD export and more than 1100 stores.',
      ],
    },
    {
      heading: 'Background of their Digital Maturity Index Decision',
      body: [
        'Digital transformation efforts at LC Waikiki have been ongoing since 2019, but the need '
        + 'for a professional to manage this process had first emerged in February of 2019. The '
        + 'LCW team began looking for its first CDO.',
        'During the last one-and-a-half-year, the pandemic slowed life down considerably. We '
        + 'began working to adapt ourselves to the new digitalising world.',
        'We prioritised four main topics. The first of these was product management, that is, we '
        + 'wanted to look at how much technology was being used in our business processes. It '
        + 'was important to know at which stages technology needed facilitation and at which '
        + 'stages raised more difficulty. The second important topic was data, which was one of '
        + 'the more popular topics. We aimed to measure how much we would benefit from data '
        + 'analytics in the decision-making processes to improve this process with artificial '
        + 'intelligence and machine learning technologies in the subsequent stages.',
        'As the next main topic, we examined our way of doing business. We reviewed our '
        + 'organizational structure and adopted the agile working principle. The last main topic '
        + 'was our technology infrastructure. We re-evaluated how modern and up-to-date the '
        + 'other technologies we use from the cloud architecture are.',
        'While we were advancing on these initiatives internally, our paths crossed with '
        + 'Digitopia. Our aim was to see the reflection of our work. We wanted to measure where '
        + 'the digital transformation actions we took brought us. This time, we wanted to '
        + 'include not only the IT team, but also business units such as marketing, human '
        + 'resources, board, and operation units; then our cooperation with Digitopia began.',
        'The main purpose of our learning about our digital maturity was to transfer the digital '
        + 'transformation process to all teams instead of experiencing it only at the level of '
        + 'IT teams. Rather than the result, we want to see in the assessment we want to make, '
        + 'how much the business units reflect on their information technologies and data '
        + 'analytics competencies while managing their own processes.',
        'A CDO managing its digital transformation journey needs to see how successful it is in '
        + 'its KPIs. Moreover, we needed to see to what extent not only CDO but also business '
        + 'units would internalize digitalization.',
      ],
    },
    {
      heading: 'What is special about working with Digitopia?',
      body: [
        'One of the main reasons we do this cooperation with Digitopia is that they did this '
        + 'with an interactive discussion method instead of a standard questionnaire. By guiding '
        + 'our teams, they made us do the interrogation, and when necessary, they intervened and '
        + 'adjusted, preventing us from giving ourselves too much or unfairly fewer points.',
        'Moreover, Digitopia, rather than others, has a method that includes six different '
        + 'dimensions with a broader perspective, instead of just dealing with IT-based '
        + 'approach. That’s the biggest difference I’ve seen.',
        'Now we have a report showing us where we are in the global market. In the roadmap '
        + 'prepared for the point that we want to go to, there is a baseline consisting of a '
        + 'prioritized action list. We had lofty goals. When the action list came out, we '
        + 'evaluated each action item one by one. We discussed which ones were actually '
        + 'achievable. After all, we do not want to do goodwill hunting, even though we have '
        + 'high goals. If we wanted to make the result more concrete, we talked together about '
        + 'what it would look like. Therefore, we have a report that is more tailor made and '
        + 'easier to explain and understand.',
        'Another useful method in the digital maturity model used by Digitopia was to make us '
        + 'think. It was not enough for us to think once while evaluating the situation. We '
        + 'talked about what we accomplished and what we missed in the previous state a year '
        + 'ago, we thought about whether we wasted time evaluating the current state and what we '
        + 'wanted to do differently in the next state we want to go to.',
        'After seeing whether our digital transformation efforts were reflected to the whole '
        + 'team, our most important need was to see an international benchmark. Seeing more data '
        + 'and insights in the fashion retail market is invaluable. How fashion retail giants '
        + 'continue their digital transformation, how they adapted to the agile working '
        + 'principle, how they manage e-commerce, how they digitized supply chain management. '
        + 'Thanks to such comparisons, we can see in which areas we are leaders and in which '
        + 'areas we need to improve. They enabled us to see more benchmark data specific to the '
        + 'sector, to the market, and to the global.',
      ],
    },
    {
      heading: 'Are you happy with the digital maturity index results?',
      body: [
        'At LC Waikiki, our strategy is tied to OKRs. One of our OKRs is now the annual Digital '
        + 'Maturity Index score. Therefore, we decided to repeat the digital maturity '
        + 'measurement every year.',
        'The biggest benefit of consulting for companies is that sometimes it makes the things '
        + 'in front of our eyes more visible and prevents it from being postponed. Since we are '
        + 'racing against time in digital transformation, we must show all our strength. When we '
        + 'decided to measure our digital maturity, we knew that the most important thing for us '
        + 'was the action list, not our score. Although our score was above the global market '
        + 'average, the points we targeted for the next state were very high. Although the '
        + 'Digitopia team warned us about this, we decided to keep our target high. Now a '
        + 'challenging journey awaits us in front of us.',
      ],
    },
  ],
  check:
    'This page’s words were transcribed from a four-part full-page capture of '
    + `${STORY_SOURCE}/lc-waikiki/ supplied on 2026-08-28, not from a file we hold, so please `
    + 'proofread it against the source before production — particularly the figures in the '
    + 'second paragraph (18.2 billion TL net sales, more than 50 thousand employees, 845 '
    + 'million USD export, more than 1100 stores, all “as of 2020”) and the 2023 ambition in '
    + 'the sentence above them, which is a target the reference states in the future tense and '
    + 'which has since passed. Four sections after the first are the CDO speaking in the first '
    + 'person — “we prioritised”, “I’ve seen” — with no attribution line on the page; that is '
    + 'the reference’s own shape, and it is worth deciding whether these words should be '
    + 'introduced as Şerafettin Özer’s, since his quote card at the foot is the only place the '
    + 'page names a person. The reference’s British spellings (“prioritised”, “digitalising”) '
    + 'are kept verbatim rather than Americanised, alongside its own American “organizational”, '
    + '“digitized” and “internalize” in the same passage. Three deliberate differences from the '
    + 'reference, each of them a template-wide decision rather than this page’s: the header '
    + 'eyebrow reads “Digitopia / Customer Success Story” where digitopia.co reads “SUCCESS '
    + 'STORIES”; the “Read the full story (PDF)” button in the header is this site’s and has no '
    + 'counterpart on the reference; and the quote card at the foot is the Hall of Fame '
    + 'template as asked for, so its eyebrow is the company where the reference would set the '
    + 'person’s name — his name and title are on the line under the quote either way. Two '
    + 'smaller readings: the reference sets double spaces mid-sentence in three places '
    + '(“wholesale brand.  Tema Tekstil”, “The  LCW team”, “considerably.  We began”), '
    + 'normalised here to one; and the second paragraph runs under no heading at all, which is '
    + 'what the capture shows rather than a heading that failed to transcribe. One production '
    + 'note: the garment-rack photograph is a 1200×455 original, which is what the live site '
    + 'serves, so it is drawn at that width and will look soft on a high-density screen until a '
    + 'larger file arrives.',
})

/** Its place in the hand-placed order the imported Customers page set. */
export const order = 8
